import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiKycOptions, apiKycStatus, apiKycUpdate } from "@/lib/api";

type CountryOption = [string, string];

const fallbackDocumentCountries: CountryOption[] = [
  ["EE", "Estonia"],
  ["SE", "Sweden"],
  ["FI", "Finland"],
  ["NO", "Norway"],
  ["DK", "Denmark"],
  ["GB", "United Kingdom"],
  ["IE", "Ireland"],
  ["DE", "Germany"],
  ["FR", "France"],
  ["ES", "Spain"],
  ["IT", "Italy"],
  ["NL", "Netherlands"],
  ["BE", "Belgium"],
  ["PL", "Poland"],
  ["LT", "Lithuania"],
  ["LV", "Latvia"],
  ["US", "United States"],
  ["CA", "Canada"],
  ["AU", "Australia"],
  ["NZ", "New Zealand"],
  ["ZZ", "Other / not listed"],
];

function requiresDocumentBack(documentType: string): boolean {
  return documentType === "national_id" || documentType === "driving_license";
}

export default function KycEdit() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [kycId, setKycId] = useState<number | null>(null);

  const [document_type, setDocumentType] =
    useState<"passport" | "national_id" | "driving_license">("passport");
  const [document_country, setDocumentCountry] = useState("");
  const [documentCountries, setDocumentCountries] = useState<CountryOption[]>(fallbackDocumentCountries);
  const [document_number, setDocumentNumber] = useState("");
  const [document_expiry, setDocumentExpiry] = useState("");

  const [mode, setMode] = useState<"webcam" | "manual">("manual");
  const [id_document, setIdDocument] = useState<File | null>(null);
  const [id_document_back, setIdDocumentBack] = useState<File | null>(null);
  const [selfie_photo, setSelfiePhoto] = useState<File | null>(null);
  const [poa_file, setPoaFile] = useState<File | null>(null);

  const [document_photo_data, setDocBase64] = useState("");
  const [document_back_photo_data, setDocBackBase64] = useState("");
  const [selfie_photo_data, setSelfieBase64] = useState("");
  const [poa_photo_data, setPoaBase64] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    apiKycOptions()
      .then((options) => {
        if (!mounted || !Array.isArray(options.document_countries)) return;

        const countries = options.document_countries
          .filter((country) => country?.code && country?.label)
          .map((country) => [country.code, country.label] as CountryOption);

        if (countries.length > 0) {
          setDocumentCountries(countries);
        }
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const s = await apiKycStatus(); // GET /xapi/kyc/status
        if (!s.kyc) {
          navigate("/kyc/create");
          return;
        }
        setKycId(Number(s.kyc.id));
        setDocumentType((s.kyc.document_type as any) || "passport");
        setDocumentCountry(s.kyc.document_country || "");
        setDocumentNumber(s.kyc.document_number || "");
        setDocumentExpiry((s.kyc.document_expiry || "").slice(0, 10));
      } catch (e: any) {
        setError(e?.message || "Failed to load KYC.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kycId) return;
    setSubmitting(true);
    setError(null);
    try {
      let payload: any;
      const needsBack = requiresDocumentBack(document_type);

      if (mode === "manual") {
        if (!document_country || !id_document || (needsBack && !id_document_back) || !selfie_photo || !poa_file) {
          throw new Error(
            needsBack
              ? "Attach ID front, ID back, selfie and proof of address for resubmission."
              : "Attach ID document, selfie and proof of address for resubmission."
          );
        }
        const fd = new FormData();
        fd.append("document_type", document_type);
        fd.append("document_country", document_country);
        fd.append("document_number", document_number);
        fd.append("document_expiry", document_expiry);
        fd.append("id_document_front", id_document);
        if (id_document_back) fd.append("id_document_back", id_document_back);
        fd.append("selfie_photo", selfie_photo);
        fd.append("poa_file", poa_file);
        payload = fd;
      } else {
        if (!document_country || !document_photo_data || (needsBack && !document_back_photo_data) || !selfie_photo_data || !poa_photo_data) {
          throw new Error("Provide ID country, document image data, selfie and proof of address to resubmit.");
        }
        payload = {
          document_type,
          document_country,
          document_number,
          document_expiry,
          document_photo_data,
          document_back_photo_data,
          selfie_photo_data,
          poa_photo_data,
        };
      }

      await apiKycUpdate(kycId, payload); // PUT /xapi/kyc/{id}
      navigate("/kyc/status");
    } catch (e: any) {
      setError(e?.message || "Failed to update KYC.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 pb-24">
      <div className="section-box">
        <h1 className="text-xl font-semibold mb-4">Update / Resubmit KYC</h1>

        {loading && <div className="skeleton h-10 w-full" />}

        {error && <div className="alert-danger mb-4">{error}</div>}

        {!loading && (
          <>
            <div className="card-box mb-4">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={`btn ${mode === "manual" ? "" : "btn-white"}`}
                  onClick={() => setMode("manual")}
                >
                  Manual Upload
                </button>
                <button
                  type="button"
                  className={`btn ${mode === "webcam" ? "" : "btn-white"}`}
                  onClick={() => setMode("webcam")}
                >
                  Webcam / Base64
                </button>
              </div>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm mb-1">Document Type</label>
                  <select
                    value={document_type}
                    onChange={(e) => setDocumentType(e.target.value as any)}
                  >
                    <option value="passport">Passport</option>
                    <option value="national_id">National ID</option>
                    <option value="driving_license">Driving License</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1">ID Country</label>
                  <select
                    value={document_country}
                    onChange={(e) => setDocumentCountry(e.target.value)}
                  >
                    <option value="">Select country</option>
                    {documentCountries.map(([code, label]) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1">Document Number</label>
                  <input
                    value={document_number}
                    onChange={(e) => setDocumentNumber(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Document Expiry</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="YYYY-MM-DD"
                    pattern="\\d{4}-\\d{2}-\\d{2}"
                    title="Use YYYY-MM-DD"
                    value={document_expiry}
                    onChange={(e) => setDocumentExpiry(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-500">Use YYYY-MM-DD.</p>
                </div>
              </div>

              {mode === "manual" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1">ID Front / Photo Page (jpg/png/pdf)</label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => setIdDocument(e.target.files?.[0] || null)}
                    />
                  </div>
                  {requiresDocumentBack(document_type) && (
                    <div>
                      <label className="block text-sm mb-1">ID Back (jpg/png/pdf)</label>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => setIdDocumentBack(e.target.files?.[0] || null)}
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm mb-1">Selfie Photo (jpg/png)</label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={(e) => setSelfiePhoto(e.target.files?.[0] || null)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">
                      Proof of Address (jpg/png/pdf)
                    </label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => setPoaFile(e.target.files?.[0] || null)}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm mb-1">Document Front Base64</label>
                    <textarea
                      rows={4}
                      value={document_photo_data}
                      onChange={(e) => setDocBase64(e.target.value)}
                    />
                  </div>
                  {requiresDocumentBack(document_type) && (
                    <div>
                      <label className="block text-sm mb-1">Document Back Base64</label>
                      <textarea
                        rows={4}
                        value={document_back_photo_data}
                        onChange={(e) => setDocBackBase64(e.target.value)}
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm mb-1">Selfie Base64</label>
                    <textarea
                      rows={4}
                      value={selfie_photo_data}
                      onChange={(e) => setSelfieBase64(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Proof of Address Base64</label>
                    <textarea
                      rows={4}
                      value={poa_photo_data}
                      onChange={(e) => setPoaBase64(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button className="btn" disabled={submitting}>
                  {submitting ? "Updating..." : "Update & Resubmit"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
